import { Translate } from '@chipinside/translate'

import { ENTITY_CONFIG, getEntityKeys } from '#/components/AdvancedSearch/Logic/EntityConfig'
import { getEntityCount } from '#/components/AdvancedSearch/Logic/useEntity'
import {
  BuildCategoryOptionsParams,
  BuildResultGroupsParams,
  CategoryOption,
  EntityKey,
  EntityResult,
  PageCategory,
  RecentGroupKey,
  RecentSearchItem,
  ResolvedSearchItem,
  ResolvedSearchResult,
  ResultGroup,
  ResultIcon,
  SearchCategory,
  SearchResultItem,
} from '#/components/AdvancedSearch/types'
import { ResolvedPage } from '#/store/services/Pages/PagesService'
import Colors from '#/styles/Colors'

/** Chave do grupo de buscas recentes, exibido quando não há texto digitado */
export const RECENT_GROUP_KEY: RecentGroupKey = 'recent'

/** Itens exibidos por grupo antes do link "ver todos" */
export const RESULTS_PER_GROUP_CAP = 5

export const PAGE_CATEGORY_ORDER: PageCategory[] = ['resources', 'lists', 'analytics']

export const PAGE_CATEGORY_LABEL_KEY: Record<PageCategory, string> = {
  analytics: 'analysis',
  lists: 'lists',
  resources: 'resources',
}

export function isPageCategory(
  category: SearchCategory | typeof RECENT_GROUP_KEY,
): category is PageCategory {
  return (PAGE_CATEGORY_ORDER as string[]).includes(category)
}

export function groupPagesByCategory(pages: ResolvedPage[]): Record<PageCategory, ResolvedPage[]> {
  const groups: Record<PageCategory, ResolvedPage[]> = { analytics: [], lists: [], resources: [] }
  pages.forEach(page => groups[page.category].push(page))
  return groups
}

export function buildCategoryOptions({
  entityData,
  isFarmContext,
  pages,
}: BuildCategoryOptionsParams): CategoryOption[] {
  const options: CategoryOption[] = []
  const entityKeys = getEntityKeys(isFarmContext)

  entityKeys.forEach(entity => {
    // Entidades ausentes no resultado não pertencem ao contexto atual
    if (!entityData?.[entity]) return

    options.push({
      count: getEntityCount(entityData, entity),
      key: entity,
      label: Translate({ messageKey: ENTITY_CONFIG[entity].titleKey }),
    })
  })

  const pageGroups = groupPagesByCategory(pages)

  PAGE_CATEGORY_ORDER.forEach(category => {
    options.push({
      count: pageGroups[category].length,
      key: category,
      label: Translate({ messageKey: PAGE_CATEGORY_LABEL_KEY[category] }),
    })
  })

  return options
}

/**
 * Grupo de buscas recentes, exibido no lugar dos grupos de entidade e página
 * Não recebe filtro de categoria nem corte de itens
 */
function buildRecentGroup(recentItems: RecentSearchItem[]): ResultGroup[] {
  if (recentItems.length === 0) return []

  return [
    {
      color: Colors.searchFilters.all,
      isActiveFilter: false,
      isCapped: false,
      items: recentItems.map(item => ({ key: item.key, kind: 'recent', recentItem: item })),
      key: RECENT_GROUP_KEY,
      label: Translate({ messageKey: 'search_recent' }),
      totalCount: recentItems.length,
    },
  ]
}

/**
 * Monta os grupos de resultado de entidades e páginas na mesma forma
 * Sem texto digitado, retorna o grupo de buscas recentes
 */
export function buildResultGroups({
  activeCategory,
  entityData,
  isFarmContext,
  pages,
  recentItems,
  searchInput,
}: BuildResultGroupsParams): ResultGroup[] {
  if (!searchInput) return buildRecentGroup(recentItems)

  const filterEntity =
    activeCategory && !isPageCategory(activeCategory) ? activeCategory : undefined
  const pageCategory = activeCategory && isPageCategory(activeCategory) ? activeCategory : null

  const groups: ResultGroup[] = []

  if (entityData && !pageCategory) {
    const entityKeys = getEntityKeys(isFarmContext).filter(
      entity => !filterEntity || entity === filterEntity,
    )

    entityKeys.forEach(entity => {
      const results = entityData[entity]?.results ?? []
      if (results.length === 0) return

      // A API já corta as entidades e devolve o total real, sem paginação nesta tela
      const totalCount = entityData[entity]?.total ?? results.length

      groups.push({
        color: Colors.searchFilters[entity],
        isActiveFilter: entity === filterEntity,
        isCapped: totalCount > RESULTS_PER_GROUP_CAP,
        items: results.map((item, index) => ({
          category: entity,
          item,
          key: `entity-${entity}-${item.slug ?? item.code ?? item.value ?? index}`,
          kind: 'entity',
        })),
        key: entity,
        label: Translate({ messageKey: ENTITY_CONFIG[entity].titleKey }),
        totalCount,
      })
    })
  }

  if (searchInput && !filterEntity) {
    const pageGroups = groupPagesByCategory(pages)

    PAGE_CATEGORY_ORDER.filter(category => !pageCategory || category === pageCategory).forEach(
      category => {
        const categoryPages = pageGroups[category]
        if (categoryPages.length === 0) return

        const isCapped = !pageCategory && categoryPages.length > RESULTS_PER_GROUP_CAP
        const visible = isCapped ? categoryPages.slice(0, RESULTS_PER_GROUP_CAP) : categoryPages

        groups.push({
          color: Colors.searchFilters[category],
          isActiveFilter: category === pageCategory,
          isCapped,
          items: visible.map(page => ({ category, key: `page-${page.id}`, kind: 'page', page })),
          key: category,
          label: Translate({ messageKey: PAGE_CATEGORY_LABEL_KEY[category] }),
          totalCount: categoryPages.length,
        })
      },
    )
  }

  return groups
}

export function getResultItemLink(result: SearchResultItem): string {
  if (result.kind === 'recent') return resolveRecentSearchItem(result.recentItem).link
  if (result.kind === 'page') return result.page.link
  return ENTITY_CONFIG[result.category].link(result.item)
}

function resolveEntityFields(entity: EntityKey, item: EntityResult) {
  const config = ENTITY_CONFIG[entity]

  return {
    description: config.description?.(item),
    label: config.text(item),
    link: config.link(item),
    locate: config.locate?.(item),
  }
}

function resolvePageFields(page: ResolvedPage) {
  return {
    crumbLabel: page.crumb,
    description: page.description,
    label: page.title,
    link: page.link,
  }
}

/**
 * Normaliza um resultado ao vivo para exibição e monta o registro de busca recente
 * O registro só existe quando o item tem identificador
 */
export function resolveSearchResultItem(result: SearchResultItem): ResolvedSearchResult {
  if (result.kind === 'recent') {
    return {
      recentSearchItem: result.recentItem,
      resolved: resolveRecentSearchItem(result.recentItem),
    }
  }

  if (result.kind === 'page') {
    const icon: ResultIcon = { category: result.category, source: 'page' }
    const fields = resolvePageFields(result.page)

    return {
      recentSearchItem: { ...fields, icon, key: result.key },
      resolved: { ...fields, icon, key: result.key },
    }
  }

  const icon: ResultIcon = {
    collar: result?.item?.collar?.code,
    entity: result.category,
    source: 'entity',
  }
  const fields = resolveEntityFields(result.category, result.item)
  // Fazendas se identificam por value; as demais entidades por slug ou code
  const hasIdentifier = Boolean(result.item.slug ?? result.item.code ?? result.item.value)

  return {
    recentSearchItem: hasIdentifier ? { icon, item: result.item, key: result.key } : undefined,
    resolved: { ...fields, icon, key: result.key },
  }
}

/**
 * Normaliza uma busca recente persistida para a mesma forma dos resultados ao vivo
 * Registros gravados sem icon.category caem no fallback resources
 */
export function resolveRecentSearchItem(recentItem: RecentSearchItem): ResolvedSearchItem {
  if (!('item' in recentItem)) {
    return {
      ...recentItem,
      icon: { category: recentItem.icon.category ?? 'resources', source: 'page' },
    }
  }

  return {
    ...resolveEntityFields(recentItem.icon.entity, recentItem.item),
    icon: recentItem.icon,
    key: recentItem.key,
  }
}
